import { useEffect, useState } from 'react';
import { Check, Plus, X } from 'lucide-react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  ICS204_DELETE_BTN,
  ICS204_HINT,
  ICS204_ICON_BTN,
  ICS204_MUTED,
  ICS204_OUTLINE_BTN,
  ICS204_TABLE,
} from './assignmentCardStyles';
import {
  emptyCommunicationDraft,
  type AssignmentCardFormData,
  type CommunicationDraft,
} from './assignmentCardFormTypes';
import {
  Ics204SelectField,
  Ics204TextField,
  type Ics204SelectOption,
} from './Ics204Fields';

const TYPE_OPTIONS: Ics204SelectOption[] = [
  { value: 'Radio', label: 'Radio' },
  { value: 'Nonradio', label: 'Nonradio' },
];

const isNonradio = (type: string | undefined) =>
  (type ?? '').toLowerCase() === 'nonradio';

const cloneDraft = (row: CommunicationDraft): CommunicationDraft => ({
  ...row,
});

const draftsEqual = (a: CommunicationDraft, b: CommunicationDraft): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

const COLUMNS = [
  'Type',
  'Function / Name',
  'Channel Name',
  'Channel #',
  'RX Freq',
  'RX Tone/NAC',
  'TX Freq',
  'TX Tone/NAC',
  'Mode',
  'Remarks',
  '',
];

/**
 * Box 8. These rows live on the incident's communications list, the same one
 * the ICS-205 edits. They are scoped by work group with no operational period
 * column, so a row added here shows on this work group's cards in every
 * period.
 */
const AssignmentCardCommunicationsEdit = () => {
  const { control, setValue, getValues } =
    useFormContext<AssignmentCardFormData>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'communications',
  });
  // `fields` is a snapshot, so archive flags and type are watched to stay live.
  const rows = useWatch({ control, name: 'communications' });

  /** RHF field-array key → last confirmed draft. */
  const [snapshots, setSnapshots] = useState<
    Record<string, CommunicationDraft>
  >({});
  const [confirmedKeys, setConfirmedKeys] = useState<Set<string>>(
    () => new Set()
  );

  // Seed confirmed snapshots for rows that already exist on the server.
  useEffect(() => {
    setSnapshots((prev) => {
      let next = prev;
      fields.forEach((field, index) => {
        const row = rows?.[index];
        if (!row || row.archived || !row.id || next[field.id]) return;
        if (next === prev) next = { ...prev };
        next[field.id] = cloneDraft(row);
      });
      return next;
    });
    setConfirmedKeys((prev) => {
      let changed = false;
      const next = new Set(prev);
      fields.forEach((field, index) => {
        const row = rows?.[index];
        if (!row || row.archived || !row.id || next.has(field.id)) return;
        next.add(field.id);
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [fields, rows]);

  const archiveOrRemove = (index: number, id: string) => {
    if (id) {
      setValue(`communications.${index}.archived`, true, { shouldDirty: true });
    } else {
      remove(index);
    }
  };

  const handleConfirm = (index: number, fieldKey: string) => {
    const row = getValues(`communications.${index}`);
    setSnapshots((prev) => ({ ...prev, [fieldKey]: cloneDraft(row) }));
    setConfirmedKeys((prev) => new Set(prev).add(fieldKey));
  };

  const handleCancel = (index: number, fieldKey: string, id: string) => {
    const isConfirmed = confirmedKeys.has(fieldKey);
    const snapshot = snapshots[fieldKey];
    const current = getValues(`communications.${index}`);

    if (!isConfirmed) {
      archiveOrRemove(index, id);
      return;
    }

    if (snapshot && !draftsEqual(snapshot, current)) {
      setValue(`communications.${index}`, cloneDraft(snapshot), {
        shouldDirty: true,
      });
      return;
    }

    archiveOrRemove(index, id);
    setSnapshots((prev) => {
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
    setConfirmedKeys((prev) => {
      const next = new Set(prev);
      next.delete(fieldKey);
      return next;
    });
  };

  const visibleCount = fields.filter(
    (field, index) => !(rows?.[index]?.archived ?? field.archived)
  ).length;

  return (
    <div data-testid="ics204-edit-communications">
      <p className={ICS204_HINT}>These channels are shared with the ICS-205.</p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={cn(ICS204_OUTLINE_BTN, 'tw:mt-2')}
        data-testid="ics204-edit-add-communication"
        onClick={() => append(emptyCommunicationDraft())}
      >
        <Plus className="tw:h-4 tw:w-4" />
        Add Communication
      </Button>
      <Table className={cn('tw:mt-2', ICS204_TABLE)}>
        <TableHeader>
          <TableRow>
            {COLUMNS.map((label, idx) => (
              <TableHead key={label || `actions-${idx}`}>{label}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleCount === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMNS.length} className={ICS204_MUTED}>
                No communications
              </TableCell>
            </TableRow>
          ) : (
            fields.map((field, index) => {
              const row = rows?.[index] ?? field;
              if (row.archived) return null;
              const base = `communications.${index}` as const;
              const radioOnly = !isNonradio(row.type);

              return (
                <TableRow
                  key={field.id}
                  data-testid="ics204-edit-communication-row"
                >
                  <TableCell>
                    <Ics204SelectField
                      fieldName={`${base}.type`}
                      testId="ics204-edit-communication-type"
                      options={TYPE_OPTIONS}
                    />
                  </TableCell>
                  <TableCell>
                    <Ics204TextField
                      fieldName={
                        radioOnly ? `${base}.function` : `${base}.other`
                      }
                      testId="ics204-edit-communication-function"
                    />
                  </TableCell>
                  <TableCell>
                    <Ics204TextField
                      fieldName={`${base}.channel_name`}
                      testId="ics204-edit-communication-channel-name"
                    />
                  </TableCell>
                  <TableCell>
                    {radioOnly ? (
                      <Ics204TextField
                        fieldName={`${base}.channel_number`}
                        testId="ics204-edit-communication-channel-number"
                      />
                    ) : (
                      <Ics204TextField
                        fieldName={`${base}.cell_phone_number`}
                        testId="ics204-edit-communication-cell-phone"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {radioOnly ? (
                      <Ics204TextField
                        fieldName={`${base}.rx_frequency`}
                        testId="ics204-edit-communication-rx-frequency"
                      />
                    ) : (
                      <span className={ICS204_MUTED}>—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {radioOnly ? (
                      <Ics204TextField
                        fieldName={`${base}.rx_tone_nac`}
                        testId="ics204-edit-communication-rx-tone"
                      />
                    ) : (
                      <span className={ICS204_MUTED}>—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {radioOnly ? (
                      <Ics204TextField
                        fieldName={`${base}.tx_frequency`}
                        testId="ics204-edit-communication-tx-frequency"
                      />
                    ) : (
                      <span className={ICS204_MUTED}>—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {radioOnly ? (
                      <Ics204TextField
                        fieldName={`${base}.tx_tone_nac`}
                        testId="ics204-edit-communication-tx-tone"
                      />
                    ) : (
                      <span className={ICS204_MUTED}>—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {radioOnly ? (
                      <Ics204TextField
                        fieldName={`${base}.mode`}
                        testId="ics204-edit-communication-mode"
                      />
                    ) : (
                      <span className={ICS204_MUTED}>—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Ics204TextField
                      fieldName={`${base}.remarks`}
                      testId="ics204-edit-communication-remarks"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="tw:flex tw:items-center tw:justify-end tw:gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Confirm communication"
                        className={ICS204_ICON_BTN}
                        data-testid="ics204-edit-confirm-communication"
                        onClick={() => handleConfirm(index, field.id)}
                      >
                        <Check className="tw:h-4 tw:w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Cancel communication"
                        className={ICS204_DELETE_BTN}
                        data-testid="ics204-edit-delete-communication"
                        onClick={() => handleCancel(index, field.id, row.id)}
                      >
                        <X className="tw:h-4 tw:w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default AssignmentCardCommunicationsEdit;
